// API客户端服务
// 基于后端API接口实现HTTP通信

import { API_CONFIG } from '@/utils/constants';
import { logger } from '@/utils/logger';
import type { DocumentAnalysis } from '@/types/document';
import type { DesignSpec, ThemeConfig } from '@/types/design';
import type {
  CreatePagePlanRequest,
  Page,
  PageContent,
  PageMergeRequest,
  PageMergeResponse,
  PagePlan,
  PartialRegenerationPreviewResponse,
  PartialRegenerationResponse,
  PageReorderRequest,
  PageSpec,
  PageSplitRequest,
  PageSplitResponse,
} from '@/types/page';
import type { GenerationTask, ApiError, ExportTaskSubmission } from '@/types/api';
import type { ApiResponse } from '@/types/common';
import type {
  DatabaseHealth,
  DashboardStats,
  DesignSpec as DatabaseDesignSpec,
  Document,
  HTMLPage,
  HtmlPageVersion,
  HtmlSetPage,
  HtmlSetVersion,
  PaginatedResponse,
  SearchDocumentsRequest,
  SystemStats,
  Task,
  WorkflowEvent,
} from '@/types/database';
import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosRequestHeaders } from 'axios';

type ConfigResponse = Record<string, unknown>;
type StatusResponse = Record<string, unknown>;
type SegmentationResponse = Record<string, unknown>;
type ChatHistoryItem = {
  role: 'user' | 'assistant';
  content: string;
};
type ChatEditHtmlResponse = {
  new_html?: string;
  message: string;
  page_version_id?: string;
  task_id?: string;
  status?: string;
  async_mode?: boolean;
};
type SavePageHtmlResponse = {
  updated?: boolean;
  html_content?: string;
  page_id?: string;
};
type OptimizePageCopyRequest = {
  title?: string;
  content?: string;
  objective?: string;
  instruction?: string;
};
type OptimizePageCopyResponse = {
  page_spec_id: string;
  title: string;
  content: string;
  objective: string;
  changes?: string[];
  model_used?: string | null;
};
type DocumentFilters = {
  status?: string;
  current_step?: string;
  document_format?: string;
  created_by?: string;
};

type UserRole = 'user' | 'admin';

type AdminUserItem = {
  id: string;
  username: string;
  role: UserRole | string;
  is_active: boolean;
  last_login_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type AdminListUsersResult = {
  items: AdminUserItem[];
  total: number;
  limit: number;
  offset: number;
};

export type WorkflowShareStatus = 'pending' | 'accepted' | 'rejected' | string;

export type WorkflowShareItem = {
  id: string;
  status: WorkflowShareStatus;
  document_id: string;
  document_title: string;
  source_owner_user_id?: string | null;
  source_owner_username?: string | null;
  target_user_id: string;
  target_username?: string | null;
  source_root_document_id?: string | null;
  source_file_hash?: string | null;
  note?: string | null;
  accepted_document_id?: string | null;
  created_at?: string | null;
  accepted_at?: string | null;
  rejected_at?: string | null;
};

type WorkflowShareListResult = {
  items: WorkflowShareItem[];
  total: number;
};

export type WorkflowShareActionResult = {
  share?: WorkflowShareItem;
  document_id?: string;
  message?: string;
};

export type ShareCandidateItem = {
  id: string;
  username: string;
};

export type DocumentShareRelation = {
  share_id: string;
  target_user_id: string;
  target_username?: string | null;
  status: WorkflowShareStatus;
  created_at?: string | null;
  accepted_document_id?: string | null;
  note?: string | null;
};

export type DocumentShareContext = {
  document_id: string;
  root_document_id?: string | null;
  root_document_title?: string | null;
  source_share?: {
    share_id: string;
    source_document_id: string;
    source_document_title?: string | null;
    source_owner_user_id?: string | null;
    source_owner_username?: string | null;
    shared_at?: string | null;
    accepted_at?: string | null;
  } | null;
  outgoing_shares: DocumentShareRelation[];
};

type UsageSummary = {
  account_id: string;
  balance_points: number;
  total_granted_points: number;
  total_consumed_points: number;
  status: string;
  last_activity_at?: string | null;
};

type UsageLedgerEntry = {
  id: string;
  entry_type: string;
  direction: 'credit' | 'debit' | string;
  points: number;
  balance_before: number;
  balance_after: number;
  related_event_id?: string | null;
  related_task_id?: string | null;
  remark?: string | null;
  created_at?: string | null;
};

type UsageMeteringEvent = {
  id: string;
  event_type: string;
  status: string;
  base_points: number;
  multiplier: number;
  final_points: number;
  model_name?: string | null;
  model_tier?: string | null;
  document_id?: string | null;
  page_plan_id?: string | null;
  page_spec_id?: string | null;
  task_id?: string | null;
  page_action?: 'generate' | 'optimize' | string | null;
  document?: { id?: string | null; title?: string | null } | null;
  page_spec?: { id?: string | null; page_type?: string | null } | null;
  created_at?: string | null;
  completed_at?: string | null;
};

type UsageEstimateResult = {
  event_type: string;
  page_type?: string | null;
  base_points: number;
  page_count: number;
  multiplier: number;
  model_tier: string;
  model_name?: string | null;
  final_points: number;
  current_balance?: number | null;
  enough: boolean;
  billable_page_count?: number;
  free_page_count?: number;
};

type AdminUsageAccountItem = {
  account_id: string;
  user_id?: string | null;
  username?: string | null;
  status: string;
  balance_points: number;
  total_granted_points: number;
  total_consumed_points: number;
  created_at?: string | null;
  updated_at?: string | null;
};

type AdminUsageAccountListResult = {
  items: AdminUsageAccountItem[];
  limit: number;
  offset: number;
};

type BatchUsageAdjustmentItem = {
  account_id: string;
  points: number;
};

type BatchUsageAdjustmentResultItem = {
  account_id: string;
  success: boolean;
  points: number;
  message?: string;
  account?: UsageSummary;
};

type BatchUsageAdjustmentResult = {
  mode: 'grant' | 'adjust';
  success_count: number;
  failed_count: number;
  items: BatchUsageAdjustmentResultItem[];
};

type GeminiQuotaSummary = {
  settings: {
    total_quota_calls: number;
    low_balance_threshold: number;
    alert_cooldown_calls: number;
  };
  usage: {
    used_calls: number;
    remaining_calls: number;
    latest_call_at?: string | null;
  };
  alert: {
    is_low_balance: boolean;
    should_alert: boolean;
    message?: string | null;
  };
};

type WorkflowEventInput = {
  document_id: string;
  event_type: string;
  from_step?: string | null;
  to_step?: string | null;
  payload?: Record<string, unknown>;
};

export type RestoreDocumentResult = {
  status: 'restored' | 'conflict_active' | 'already_active' | 'not_found' | 'invalid_id' | string;
  restored?: boolean;
  message?: string;
  document?: Document;
  active_document_id?: string;
  active_page_plan_id?: string;
  active_document_title?: string;
};

export type RecycleBatchItemResult = {
  document_id: string;
  status: string;
  message?: string;
  restored?: boolean;
  purged?: boolean;
  active_document_id?: string;
  active_page_plan_id?: string;
  active_document_title?: string;
  deleted_files?: string[];
};

export type RecycleBatchActionResult = {
  items: RecycleBatchItemResult[];
  summary: {
    requested: number;
    restored?: number;
    purged?: number;
    conflicts?: number;
  };
};

export type LogoPresetItem = {
  logo_id: string;
  name: string;
  source_type: 'preset';
  image_url: string;
};

export type LogoHistoryItem = {
  plan_id?: string;
  logo_id?: string | null;
  name?: string;
  source_type: 'preset' | 'upload' | 'history' | string;
  image_url: string;
  used_at?: string;
  used_for_content?: boolean;
  used_for_functional?: boolean;
};

export type LogoRecommendationItem = {
  logo_id?: string | null;
  name?: string;
  source_type: 'preset' | 'upload' | 'history' | string;
  image_url: string;
  frequency: number;
  latest_used_at?: string;
  score: number;
};

export type LogoHistoryResponse = {
  history: LogoHistoryItem[];
  recommended: LogoRecommendationItem[];
};

export type LogoUploadResult = {
  logo_id: string;
  name: string;
  source_type: 'upload';
  image_url: string;
  size_bytes: number;
  uploaded_at: string;
};

type AuthUserInfo = {
  id: string;
  username: string;
  role: string;
};

type AuthLoginResult = {
  access_token: string;
  token_type: string;
  user: AuthUserInfo;
};

type TaskDisplayStatus = 'queued' | 'processing' | 'retrying' | 'completed' | 'failed' | 'cancelled';

const AUTH_TOKEN_KEY = 'ppt_agent_access_token';
const AUTH_USER_KEY = 'ppt_agent_user';

const getAccessToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
};

const setStoredUser = (user: AuthUserInfo | null): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (!user) {
      window.localStorage.removeItem(AUTH_USER_KEY);
      return;
    }
    window.localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  } catch {
    return;
  }
};

const clearAccessToken = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
    window.localStorage.removeItem(AUTH_USER_KEY);
  } catch {
    return;
  }
};

const parseFilenameFromContentDisposition = (value: string | undefined): string | null => {
  if (!value) return null;
  const utf8Match = value.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }
  const plainMatch = value.match(/filename\s*=\s*("?)([^";]+)\1/i);
  if (plainMatch && plainMatch[2]) {
    return plainMatch[2].trim();
  }
  return null;
};

const guessFilenameFromUrl = (fileUrl: string): string | null => {
  try {
    const url = new URL(fileUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const base = url.pathname.split('/').filter(Boolean).pop() || '';
    return base ? decodeURIComponent(base) : null;
  } catch {
    const base = fileUrl.split('?')[0].split('#')[0].split('/').filter(Boolean).pop() || '';
    return base || null;
  }
};

const resolveTaskDisplay = (record: Record<string, unknown>): {
  display_status: TaskDisplayStatus;
  display_label: string;
  display_message: string;
} => {
  const rawStatus = String(record.status || 'pending').toLowerCase();
  const rawMessage = String(record.message || '').trim();
  const backendDisplayStatus = String(record.display_status || '').trim().toLowerCase();
  const backendDisplayLabel = String(record.display_label || '').trim();
  const backendDisplayMessage = String(record.display_message || '').trim();
  const errorData = (record.error_data && typeof record.error_data === 'object')
    ? record.error_data as Record<string, unknown>
    : {};
  const retryAttempt = Number(errorData.retry_attempt || 0);
  const nextRetryInSeconds = Number(errorData.next_retry_in_seconds || 0);

  if (backendDisplayStatus) {
    return {
      display_status: backendDisplayStatus as TaskDisplayStatus,
      display_label: backendDisplayLabel || '处理中',
      display_message: backendDisplayMessage || rawMessage || '系统正在处理任务',
    };
  }

  if (rawStatus === 'queued_retry') {
    if (rawMessage) {
      return { display_status: 'retrying', display_label: '自动重试中', display_message: rawMessage };
    }
    if (retryAttempt > 0 && nextRetryInSeconds > 0) {
      return {
        display_status: 'retrying',
        display_label: '自动重试中',
        display_message: `系统正在自动重试，第 ${retryAttempt} 次，约 ${nextRetryInSeconds}s 后继续`,
      };
    }
    return { display_status: 'retrying', display_label: '自动重试中', display_message: '系统正在自动重试' };
  }
  if (rawStatus === 'pending') {
    return { display_status: 'queued', display_label: '排队中', display_message: rawMessage || '任务已进入队列，等待调度' };
  }
  if (rawStatus === 'processing') {
    return { display_status: 'processing', display_label: '处理中', display_message: rawMessage || '系统正在处理任务' };
  }
  if (rawStatus === 'completed') {
    return { display_status: 'completed', display_label: '已完成', display_message: rawMessage || '任务已完成' };
  }
  if (rawStatus === 'failed') {
    return { display_status: 'failed', display_label: '失败', display_message: rawMessage || '任务处理失败' };
  }
  if (rawStatus === 'cancelled') {
    return { display_status: 'cancelled', display_label: '已取消', display_message: rawMessage || '任务已取消' };
  }
  return { display_status: 'processing', display_label: '处理中', display_message: rawMessage || '系统正在处理任务' };
};

/**
 * API客户端类
 * 提供与后端API通信的统一接口
 */
export class APIClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private axiosInstance: AxiosInstance;
  private usageSummaryCache: { value: UsageSummary; expiresAt: number } | null = null;
  private usageSummaryInflight: Promise<UsageSummary> | null = null;

  constructor(baseURL: string = API_CONFIG.BASE_URL) {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };

    // 创建axios实例
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: API_CONFIG.TIMEOUT,
      withCredentials: true,
    });

    this.axiosInstance.interceptors.request.use((config) => {
      const token = getAccessToken();
      if (!token) {
        return config;
      }
      const existingHeaders = config.headers;
      if (existingHeaders && typeof (existingHeaders as { set?: unknown }).set === 'function') {
        (existingHeaders as unknown as { set: (key: string, value: string) => void }).set('Authorization', `Bearer ${token}`);
        config.headers = existingHeaders;
        return config;
      }
      const headerRecord: Record<string, unknown> =
        existingHeaders && typeof existingHeaders === 'object' ? (existingHeaders as Record<string, unknown>) : {};
      const merged = { ...headerRecord, Authorization: `Bearer ${token}` };
      config.headers = merged as unknown as AxiosRequestHeaders;
      return config;
    });
  }

  getBaseURL(): string {
    return this.baseURL;
  }

  private invalidateUsageSummaryCache(): void {
    this.usageSummaryCache = null;
    this.usageSummaryInflight = null;
  }

  private resolveAssetOrigin(): string {
    const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const normalizedBase = String(this.baseURL || '').trim();
    if (!normalizedBase) {
      return fallbackOrigin;
    }
    if (normalizedBase.startsWith('/')) {
      return fallbackOrigin;
    }
    try {
      const resolved = new URL(normalizedBase, fallbackOrigin || 'http://localhost');
      return resolved.origin || fallbackOrigin;
    } catch {
      return fallbackOrigin;
    }
  }

  private normalizeHtmlAssetUrls(htmlContent: string | undefined | null): string {
    const raw = String(htmlContent || '');
    if (!raw) return raw;
    const assetOrigin = this.resolveAssetOrigin().replace(/\/+$/, '');
    if (!assetOrigin) return raw;

    const normalized = raw
      .replace(
        /(\b(?:src|href|poster)\s*=\s*["'])\/uploads\//gi,
        `$1${assetOrigin}/uploads/`
      )
      .replace(
        /url\((['"]?)\/uploads\//gi,
        `url($1${assetOrigin}/uploads/`
      );

    return normalized;
  }

  get<T>(endpoint: string, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  post<T>(endpoint: string, data?: unknown, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'POST', data });
  }

  put<T>(endpoint: string, data?: unknown, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', data });
  }

  patch<T>(endpoint: string, data?: unknown, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', data });
  }

  delete<T>(endpoint: string, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  async downloadProtectedFile(fileUrl: string, fallbackFilename?: string): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }
    let response: Awaited<ReturnType<typeof this.axiosInstance.get<Blob>>>;
    try {
      response = await this.axiosInstance.get<Blob>(fileUrl, {
        responseType: 'blob',
      });
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ error?: string }>;
      const status = axiosError.response?.status || 0;
      if (status === 401) {
        clearAccessToken();
        const current = window.location.pathname + window.location.search + window.location.hash;
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = `/login?redirect=${encodeURIComponent(current)}`;
        }
      }
      throw error;
    }
    const rawHeaders = response.headers as unknown as Record<string, unknown>;
    const cdCandidate = rawHeaders['content-disposition'] ?? rawHeaders['Content-Disposition'];
    const contentDisposition = typeof cdCandidate === 'string' ? cdCandidate : undefined;
    const filenameFromHeader = parseFilenameFromContentDisposition(contentDisposition);
    const filename = filenameFromHeader || fallbackFilename || guessFilenameFromUrl(fileUrl) || 'download';
    const blobUrl = window.URL.createObjectURL(response.data);
    try {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      window.URL.revokeObjectURL(blobUrl);
    }
  }

  private approxJsonSize(data: unknown): number | null {
    try {
      return JSON.stringify(data).length;
    } catch {
      return null;
    }
  }

  private summarizeLogData(data: unknown): unknown {
    if (data === null || data === undefined) return data;
    const t = typeof data;
    if (t === 'string') {
      const value = String(data);
      return { type: 'string', length: value.length, preview: value.slice(0, 200) };
    }
    if (t === 'number' || t === 'boolean') return data;
    if (Array.isArray(data)) return { type: 'array', length: data.length };
    if (t === 'object') {
      const record = data as Record<string, unknown>;
      const keys = Object.keys(record);
      const summary: Record<string, unknown> = { type: 'object', keyCount: keys.length, keys: keys.slice(0, 20) };
      if (typeof record.success === 'boolean') summary.success = record.success;
      if (typeof record.code === 'string') summary.code = record.code;
      if (typeof record.error === 'string') summary.error = record.error.slice(0, 200);
      if ('data' in record) {
        const dataValue = record.data;
        summary.dataType = Array.isArray(dataValue) ? 'array' : typeof dataValue;
      }
      const approx = this.approxJsonSize(record);
      if (approx !== null) summary.approxJsonChars = approx;
      return summary;
    }
    return { type: t };
  }

  /**
   * 通用请求方法
   */
  private async request<T>(
    endpoint: string,
    options: AxiosRequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const method = options.method || 'GET';
    const url = endpoint;

    // 记录API调用
    logger.apiCall(method, `${this.baseURL}${endpoint}`, this.summarizeLogData(options.data));

    try {
      // 为JSON请求添加Content-Type头部
      const requestOptions = { ...options };
      if (requestOptions.data && typeof requestOptions.data === 'string') {
        requestOptions.headers = {
          'Content-Type': 'application/json',
          ...requestOptions.headers,
        };
      }

      const response = await this.axiosInstance.request({
        url,
        method,
        ...requestOptions,
      });

      const responseData = response.data;

      // 记录API响应
      logger.apiResponse(method, `${this.baseURL}${endpoint}`, response.status, this.summarizeLogData(responseData));

      if (responseData.success === false) {
        const error: ApiError = {
          message: responseData.error || `HTTP ${response.status}`,
          status: response.status,
          code: responseData.code || 'UNKNOWN_ERROR',
          details: responseData.details || {},
        };

        throw error;
      }

      return responseData as ApiResponse<T>;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ error?: string; detail?: string; message?: string }>;
      const status = axiosError.response?.status || 0;
      const errorMessage = axiosError.response?.data?.error
        || axiosError.response?.data?.detail
        || axiosError.response?.data?.message
        || axiosError.message
        || '网络请求失败';

      if (status === 401) {
        clearAccessToken();
        if (typeof window !== 'undefined') {
          const current = window.location.pathname + window.location.search + window.location.hash;
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = `/login?redirect=${encodeURIComponent(current)}`;
          }
        }
      }

      const apiError: ApiError = {
        message: errorMessage,
        status: status,
        code: 'NETWORK_ERROR',
        details: { originalError: axiosError },
      };

      logger.error('API请求失败', apiError, 'APIClient');
      throw apiError;
    }
  }

  /**
   * 上传文档
   * 对应后端pipeline.process_document
   */
  async uploadDocument(file: File): Promise<DocumentAnalysis> {
    const formData = new FormData();
    formData.append('file', file);

    // 对于FormData，需要特殊处理，不能使用默认的request方法
    try {
      // 对于FormData，让axios自动设置Content-Type（包括边界信息）
      const response = await this.axiosInstance.post('/api/v1/documents/upload', formData);

      const responseData = response.data;

      if (responseData.success === false) {
        throw new Error(responseData.error || '上传失败');
      }

      return responseData.data!;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ error?: string }>;
      const status = axiosError.response?.status || 0;
      const errorMessage = axiosError.response?.data?.error || axiosError.message || '网络请求失败';

      const apiError = {
        message: errorMessage,
        status: status,
        code: 'NETWORK_ERROR',
        details: { originalError: axiosError },
      };

      logger.error('API请求失败', apiError, 'APIClient');
      throw apiError;
    }
  }

  async resolveUploadRouting(payload: {
    decision_token: string;
    action: 'attach_active' | 'restore_deleted' | 'create_new';
    target_document_id?: string;
  }): Promise<Record<string, unknown>> {
    const response = await this.request<Record<string, unknown>>('/api/v1/documents/upload/resolve', {
      method: 'POST',
      data: JSON.stringify(payload),
    });

    if (!response.success) {
      throw new Error(response.error || '处理上传决策失败');
    }

    return response.data || {};
  }

  async createWorkflowShare(payload: {
    document_id: string;
    target_user_id?: string;
    target_username?: string;
    note?: string;
  }): Promise<WorkflowShareItem> {
    const response = await this.request<WorkflowShareItem>('/api/v1/workflow-shares', {
      method: 'POST',
      data: JSON.stringify(payload),
    });

    if (!response.success) {
      throw new Error(response.error || '创建分享失败');
    }

    return response.data!;
  }

  async getIncomingWorkflowShares(status?: WorkflowShareStatus): Promise<WorkflowShareListResult> {
    const response = await this.request<WorkflowShareListResult>('/api/v1/workflow-shares/incoming', {
      params: status ? { status } : undefined,
    });

    if (!response.success) {
      throw new Error(response.error || '获取收到的分享失败');
    }

    return response.data!;
  }

  async getOutgoingWorkflowShares(status?: WorkflowShareStatus): Promise<WorkflowShareListResult> {
    const response = await this.request<WorkflowShareListResult>('/api/v1/workflow-shares/outgoing', {
      params: status ? { status } : undefined,
    });

    if (!response.success) {
      throw new Error(response.error || '获取发出的分享失败');
    }

    return response.data!;
  }

  async acceptWorkflowShare(shareId: string): Promise<WorkflowShareActionResult> {
    const response = await this.request<WorkflowShareActionResult>(`/api/v1/workflow-shares/${shareId}/accept`, {
      method: 'POST',
    });

    if (!response.success) {
      throw new Error(response.error || '接收分享失败');
    }

    return response.data || {};
  }

  async rejectWorkflowShare(shareId: string): Promise<WorkflowShareActionResult> {
    const response = await this.request<WorkflowShareActionResult>(`/api/v1/workflow-shares/${shareId}/reject`, {
      method: 'POST',
    });

    if (!response.success) {
      throw new Error(response.error || '拒绝分享失败');
    }

    return response.data || {};
  }

  async getShareCandidates(query?: string, limit = 10): Promise<ShareCandidateItem[]> {
    const response = await this.request<ShareCandidateItem[]>('/api/v1/users/share-candidates', {
      params: {
        q: query || undefined,
        limit,
      },
    });

    if (!response.success) {
      throw new Error(response.error || '获取分享候选用户失败');
    }

    return response.data || [];
  }

  /**
   * 获取设计规范列表
   * 注意：此方法已弃用，请使用getAllDesignSpecsDB()从数据库加载设计规范
   * 保持此方法用于向后兼容，但实际从数据库加载数据
   */
  async getDesignSpecs(): Promise<DesignSpec[]> {
    console.warn('getDesignSpecs()已弃用，请使用getAllDesignSpecsDB()从数据库加载设计规范');

    try {
      // 尝试从数据库加载设计规范
      const dbSpecs = await this.getAllDesignSpecsDB();

      // 将数据库设计规范转换为前端设计规范格式
      // 注意：这是简化的转换，实际可能需要更复杂的逻辑
      const designSpecs: DesignSpec[] = [];

      // 这里可以根据需要从数据库设计规范中提取信息
      // 例如，创建一个默认的设计规范组合
      if (dbSpecs.length > 0) {
        // 尝试找到默认的布局、主题和字体
        const defaultLayout = dbSpecs.find(s => s.spec_type === 'layout' && s.is_default);
        const defaultTheme = dbSpecs.find(s => s.spec_type === 'theme' && s.is_default);
        const defaultTypography = dbSpecs.find(s => s.spec_type === 'typography' && s.is_default);

        if (defaultLayout && defaultTheme && defaultTypography) {
          designSpecs.push({
            layout: defaultLayout.name,
            theme: defaultTheme.name,
            typography: defaultTypography.name,
            template_name: 'default',
            design_consistency: {}
          });
        }
      }

      return designSpecs;
    } catch (error) {
      console.error('从数据库加载设计规范失败，返回空数组:', error);
      return [];
    }
  }

  /**
   * 获取布局配置详情
   * 注意：此方法已弃用，请使用getAllDesignSpecsDB()从数据库加载设计规范
   * 保持此方法用于向后兼容，但实际从数据库加载数据
   */
  async getLayoutConfig(layoutName: string): Promise<ConfigResponse> {
    console.warn('getLayoutConfig()已弃用，请使用getAllDesignSpecsDB()从数据库加载设计规范');

    try {
      // 从数据库加载所有设计规范
      const dbSpecs = await this.getAllDesignSpecsDB();

      // 查找指定的布局规范
      const layoutSpec = dbSpecs.find(s => s.spec_type === 'layout' && s.name === layoutName);

      if (!layoutSpec) {
        throw new Error(`布局规范 "${layoutName}" 不存在于数据库中`);
      }

      // 返回配置
      return {
        ...layoutSpec.config as Record<string, unknown>,
        _db_id: layoutSpec.id,
        _version: layoutSpec.version,
        _spec_type: layoutSpec.spec_type,
        _is_active: layoutSpec.is_active,
        _is_default: layoutSpec.is_default
      };
    } catch (error) {
      console.error(`从数据库加载布局配置 "${layoutName}" 失败:`, error);
      throw new Error(`获取布局配置失败: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * 获取主题配置详情
   * 注意：此方法已弃用，请使用getAllDesignSpecsDB()从数据库加载设计规范
   * 保持此方法用于向后兼容，但实际从数据库加载数据
   */
  async getThemeConfig(themeName: string): Promise<ConfigResponse> {
    console.warn('getThemeConfig()已弃用，请使用getAllDesignSpecsDB()从数据库加载设计规范');

    try {
      // 从数据库加载所有设计规范
      const dbSpecs = await this.getAllDesignSpecsDB();

      // 查找指定的主题规范
      const themeSpec = dbSpecs.find(s => s.spec_type === 'theme' && s.name === themeName);

      if (!themeSpec) {
        throw new Error(`主题规范 "${themeName}" 不存在于数据库中`);
      }

      // 返回配置
      return {
        ...themeSpec.config as Record<string, unknown>,
        _db_id: themeSpec.id,
        _version: themeSpec.version,
        _spec_type: themeSpec.spec_type,
        _is_active: themeSpec.is_active,
        _is_default: themeSpec.is_default
      };
    } catch (error) {
      console.error(`从数据库加载主题配置 "${themeName}" 失败:`, error);
      throw new Error(`获取主题配置失败: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * 获取字体配置详情
   * 注意：此方法已弃用，请使用getAllDesignSpecsDB()从数据库加载设计规范
   * 保持此方法用于向后兼容，但实际从数据库加载数据
   */
  async getTypographyConfig(typographyName: string): Promise<ConfigResponse> {
    console.warn('getTypographyConfig()已弃用，请使用getAllDesignSpecsDB()从数据库加载设计规范');

    try {
      // 从数据库加载所有设计规范
      const dbSpecs = await this.getAllDesignSpecsDB();

      // 查找指定的字体规范
      const typographySpec = dbSpecs.find(s => s.spec_type === 'typography' && s.name === typographyName);

      if (!typographySpec) {
        throw new Error(`字体规范 "${typographyName}" 不存在于数据库中`);
      }

      // 返回配置
      return {
        ...typographySpec.config as Record<string, unknown>,
        _db_id: typographySpec.id,
        _version: typographySpec.version,
        _spec_type: typographySpec.spec_type,
        _is_active: typographySpec.is_active,
        _is_default: typographySpec.is_default
      };
    } catch (error) {
      console.error(`从数据库加载字体配置 "${typographyName}" 失败:`, error);
      throw new Error(`获取字体配置失败: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * 创建页面规划
   * 对应后端POST /api/page-plans
   */
  async createPagePlan(pagePlanData: CreatePagePlanRequest): Promise<PagePlan> {
    const response = await this.request<PagePlan>('/api/v1/page-plans', {
      method: 'POST',
      data: JSON.stringify(pagePlanData),
    });

    if (!response.success) {
      throw new Error(response.error || '创建页面规划失败');
    }

    return response.data!;
  }

  /**
   * 提交页面规划（兼容旧名称）
   */
  async submitPagePlan(pagePlan: CreatePagePlanRequest): Promise<PagePlan> {
    return this.createPagePlan(pagePlan);
  }

  /**
   * 获取页面规划详情
   * 对应后端GET /api/page-plans/{page_plan_id}
   */
  async getPagePlan(pagePlanId: string): Promise<PagePlan> {
    const response = await this.request<PagePlan>(`/api/v1/page-plans/${pagePlanId}`);

    if (!response.success) {
      throw new Error(response.error || '获取页面规划失败');
    }

    return response.data!;
  }

  /**
   * 生成页面规格
   * 对应后端POST /api/page-plans/{page_plan_id}/page-specs/generate
   */
  async generatePageSpecs(pagePlanId: string): Promise<PagePlan> {
    const response = await this.request<PagePlan>(`/api/v1/page-plans/${pagePlanId}/page-specs/generate`, {
      method: 'POST',
    });

    if (!response.success) {
      throw new Error(response.error || '生成页面规格失败');
    }

    return response.data!;
  }

  async optimizePageCopy(
    pagePlanId: string,
    pageSpecId: string,
    payload: OptimizePageCopyRequest
  ): Promise<OptimizePageCopyResponse> {
    const response = await this.request<OptimizePageCopyResponse>(
      `/api/v1/page-plans/${pagePlanId}/pages/${pageSpecId}/optimize-copy`,
      {
        method: 'POST',
        data: JSON.stringify(payload),
      }
    );

    if (!response.success) {
      throw new Error(response.error || 'AI 优化文案失败');
    }

    return response.data!;
  }

  /**
   * 更新页面规划
   * 对应后端PUT /api/page-plans/{page_plan_id}
   */
  async updatePagePlan(pagePlanId: string, pagePlan: Partial<PagePlan>): Promise<PagePlan> {
    const response = await this.request<PagePlan>(`/api/v1/page-plans/${pagePlanId}`, {
      method: 'PUT',
      data: JSON.stringify(pagePlan),
    });

    if (!response.success) {
      throw new Error(response.error || '更新页面规划失败');
    }

    return response.data!;
  }

  async confirmDesignAnchor(
    pagePlanId: string,
    payload: {
      page_spec_id: string;
      html_version_id?: string;
      page_number?: number;
      title?: string;
      preset_id?: string;
    }
  ): Promise<PagePlan> {
    const response = await this.request<PagePlan>(`/api/v1/page-plans/${pagePlanId}/design-anchor/confirm`, {
      method: 'POST',
      data: JSON.stringify(payload),
    });
    if (!response.success) {
      throw new Error(response.error || '确认锚定页失败');
    }
    return response.data!;
  }

  async getMasterSnapshot(pagePlanId: string): Promise<Record<string, unknown>> {
    const response = await this.request<Record<string, unknown>>(`/api/v1/page-plans/${pagePlanId}/master-snapshot`);
    if (!response.success) {
      throw new Error(response.error || '获取整套风格快照失败');
    }
    return response.data || {};
  }

  async getStyleDriftReport(pagePlanId: string): Promise<Record<string, unknown>> {
    const response = await this.request<Record<string, unknown>>(`/api/v1/page-plans/${pagePlanId}/style-drift-report`);
    if (!response.success) {
      throw new Error(response.error || '获取风格差异报告失败');
    }
    return response.data || {};
  }

  async rebuildMasterSnapshot(pagePlanId: string): Promise<PagePlan> {
    const response = await this.request<PagePlan>(`/api/v1/page-plans/${pagePlanId}/master-snapshot/rebuild`, {
      method: 'POST',
    });
    if (!response.success) {
      throw new Error(response.error || '重新同步整套风格失败');
    }
    return response.data!;
  }

  async applyMissingItemAction(
    pagePlanId: string,
    payload: {
      missing_id?: string;
      missing_ids?: string[];
      action: 'ACCEPT_AS_IS' | 'PATCH_EXISTING_PAGE' | 'ADD_NEW_PAGE' | 'REPLAN_STRUCTURE';
      target_page_id?: string;
    }
  ): Promise<PagePlan> {
    const response = await this.request<PagePlan>(`/api/v1/page-plans/${pagePlanId}/missing-items/actions`, {
      method: 'POST',
      data: JSON.stringify(payload),
    });

    if (!response.success) {
      throw new Error(response.error || '执行缺失项操作失败');
    }

    return response.data!;
  }

  async updatePresentationStructure(
    pagePlanId: string,
    payload: {
      ordered_chapter_ids?: string[];
      ordered_content_page_ids?: string[];
      chapter_updates?: Array<{
        chapter_id: string;
        chapter_title?: string;
        chapter_goal?: string;
        chapter_role?: string;
        chapter_key?: string;
        path?: string[];
        metadata?: Record<string, unknown>;
      }>;
      page_assignments?: Array<{
        page_spec_id: string;
        chapter_id: string;
        chapter_title?: string;
        chapter_goal?: string;
        chapter_role?: string;
        chapter_key?: string;
        path?: string[];
        metadata?: Record<string, unknown>;
      }>;
      storyline?: string;
      special_slide_policy?: Record<string, unknown>;
    }
  ): Promise<PagePlan> {
    const response = await this.request<PagePlan>(`/api/v1/page-plans/${pagePlanId}/presentation-structure`, {
      method: 'PUT',
      data: JSON.stringify(payload),
    });

    if (!response.success) {
      throw new Error(response.error || '更新演示结构失败');
    }

    return response.data!;
  }

  async updateSpecialSlideSettings(
    pagePlanId: string,
    payload: {
      special_slide_policy: Record<string, unknown>;
      policy_version?: number;
    }
  ): Promise<PagePlan> {
    const response = await this.request<PagePlan>(`/api/v1/page-plans/${pagePlanId}/special-slides/settings`, {
      method: 'PUT',
      data: JSON.stringify(payload),
    });

    if (!response.success) {
      throw new Error(response.error || '更新特殊页面配置失败');
    }

    return response.data!;
  }

  async previewPartialRegeneration(
    pagePlanId: string,
    payload: {
      page_spec_ids?: string[];
      chapter_ids?: string[];
      regeneration_mode?: string;
    }
  ): Promise<PartialRegenerationPreviewResponse> {
    const response = await this.request<PartialRegenerationPreviewResponse>(`/api/v1/page-plans/${pagePlanId}/regenerate-partial/preview`, {
      method: 'POST',
      data: JSON.stringify(payload),
    });

    if (!response.success) {
      throw new Error(response.error || '预览局部重生范围失败');
    }

    return response.data!;
  }

  async regeneratePartial(
    pagePlanId: string,
    payload: {
      page_spec_ids?: string[];
      chapter_ids?: string[];
      regeneration_mode?: string;
      start_generation_task?: boolean;
      options?: Record<string, unknown>;
    }
  ): Promise<PartialRegenerationResponse> {
    const response = await this.request<PartialRegenerationResponse>(`/api/v1/page-plans/${pagePlanId}/regenerate-partial`, {
      method: 'POST',
      data: JSON.stringify(payload),
    });

    if (!response.success) {
      throw new Error(response.error || '局部重生失败');
    }

    return response.data!;
  }

  /**
   * 删除页面规划
   * 对应后端DELETE /api/page-plans/{page_plan_id}
   */
  async deletePagePlan(pagePlanId: string): Promise<boolean> {
    const response = await this.request<{ deleted: boolean }>(`/api/v1/page-plans/${pagePlanId}`, {
      method: 'DELETE',
    });

    if (!response.success) {
      throw new Error(response.error || '删除页面规划失败');
    }

    return response.data!.deleted;
  }

  /**
   * 获取文档的页面规划列表
   * 对应后端GET /api/documents/{document_id}/page-plans
   */
  async getDocumentPagePlans(documentId: string): Promise<PagePlan[]> {
    const response = await this.request<PagePlan[]>(`/api/v1/documents/${documentId}/page-plans`);

    if (!response.success) {
      throw new Error(response.error || '获取文档页面规划列表失败');
    }

    return response.data!;
  }

  async getHtmlPageVersions(pageSpecId: string): Promise<HtmlPageVersion[]> {
    const response = await this.request<HtmlPageVersion[]>(`/api/v1/html-versions/page-specs/${pageSpecId}/pages`);

    if (!response.success) {
      throw new Error(response.error || '获取HTML页面版本失败');
    }

    const versions = response.data || [];
    return versions.map((item) => ({
      ...item,
      html_content: this.normalizeHtmlAssetUrls(item?.html_content),
    }));
  }

  async createHtmlPageVersion(
    pageSpecId: string,
    payload: {
      page_number: number;
      html_content: string;
      render_params?: Record<string, unknown>;
      generation_config?: Record<string, unknown>;
      model_used?: string;
      validation_passed?: boolean;
      validation_notes?: string;
      assets?: unknown;
    }
  ): Promise<HtmlPageVersion> {
    const response = await this.request<HtmlPageVersion>(
      `/api/v1/html-versions/page-specs/${pageSpecId}/pages`,
      {
        method: 'POST',
        data: JSON.stringify(payload),
      }
    );

    if (!response.success) {
      throw new Error(response.error || '创建HTML页面版本失败');
    }

    return response.data!;
  }

  async getHtmlSetVersion(pagePlanId: string): Promise<HtmlSetVersion | null> {
    const response = await this.request<HtmlSetVersion | null>(`/api/v1/html-versions/page-plans/${pagePlanId}/set`);

    if (!response.success) {
      throw new Error(response.error || '获取HTML集合版本失败');
    }

    return response.data ?? null;
  }

  async createHtmlSetVersion(
    pagePlanId: string,
    payload: {
      layout_params?: Record<string, unknown>;
      render_settings?: Record<string, unknown>;
      created_by?: string;
    }
  ): Promise<HtmlSetVersion> {
    const response = await this.request<HtmlSetVersion>(
      `/api/v1/html-versions/page-plans/${pagePlanId}/set`,
      {
        method: 'POST',
        data: JSON.stringify(payload),
      }
    );

    if (!response.success) {
      throw new Error(response.error || '创建HTML集合版本失败');
    }

    return response.data!;
  }

  async getHtmlSetPages(htmlSetVersionId: string): Promise<HtmlSetPage[]> {
    const response = await this.request<HtmlSetPage[]>(`/api/v1/html-versions/sets/${htmlSetVersionId}/pages`);

    if (!response.success) {
      throw new Error(response.error || '获取HTML集合页面失败');
    }

    const pages = response.data || [];
    return pages.map((item) => ({
      ...item,
      html_content: this.normalizeHtmlAssetUrls(item?.html_content),
    }));
  }


  /**
   * 获取文档状态和工作流信息
   * 对应后端GET /api/documents/{document_id}/status
   */
  async getDocumentStatus(
    documentId: string,
    options: { includeEvents?: boolean; eventLimit?: number } = {}
  ): Promise<StatusResponse> {
    const includeEvents = Boolean(options?.includeEvents);
    const eventLimit = Number.isFinite(options?.eventLimit) ? Number(options.eventLimit) : undefined;
    const params = new URLSearchParams();
    if (includeEvents) {
      params.set('include_events', 'true');
    }
    if (eventLimit && eventLimit > 0) {
      params.set('event_limit', String(eventLimit));
    }
    const query = params.toString();
    const url = query ? `/api/v1/documents/${documentId}/status?${query}` : `/api/v1/documents/${documentId}/status`;

    const response = await this.request<StatusResponse>(url);

    if (!response.success) {
      throw new Error(response.error || '获取文档状态失败');
    }

    return response.data!;
  }

  /**
   * 创建页面规格
   */
  async createPageSpec(pageSpecData: PageSpec): Promise<PageSpec> {
    const response = await this.request<PageSpec>('/api/v1/page-specs', {
      method: 'POST',
      data: JSON.stringify(pageSpecData),
    });

    if (!response.success) {
      throw new Error(response.error || '创建页面规格失败');
    }

    return response.data!;
  }

  /**
   * 更新页面规格
   */
  async updatePageSpec(pageSpecId: string, pageSpecData: Partial<PageSpec>): Promise<PageSpec> {
    const response = await this.request<PageSpec>(`/api/v1/page-specs/${pageSpecId}`, {
      method: 'PUT',
      data: JSON.stringify(pageSpecData),
    });

    if (!response.success) {
      throw new Error(response.error || '更新页面规格失败');
    }

    return response.data!;
  }

  async uploadPageSpecMaterialImage(pageSpecId: string, file: File): Promise<Record<string, unknown>> {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await this.axiosInstance.post(`/api/v1/page-specs/${pageSpecId}/materials/upload-image`, formData);
      const responseData = response.data;
      if (responseData.success === false) {
        throw new Error(responseData.error || '上传页面素材图片失败');
      }
      return responseData.data as Record<string, unknown>;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ error?: string; detail?: string }>;
      const errorMessage = axiosError.response?.data?.error
        || axiosError.response?.data?.detail
        || axiosError.message
        || '上传页面素材图片失败';
      throw new Error(errorMessage);
    }
  }

  async updatePageMasterOverrides(
    pageSpecId: string,
    masterOverrides: Record<string, unknown>
  ): Promise<PageSpec> {
    const response = await this.request<PageSpec>(`/api/v1/page-specs/${pageSpecId}/master-overrides`, {
      method: 'PUT',
      data: JSON.stringify({ master_overrides: masterOverrides }),
    });
    if (!response.success) {
      throw new Error(response.error || '更新页面本页调整失败');
    }
    return response.data!;
  }

  async getPagePreview(documentId: string, pageId: string): Promise<{
    page_id: string;
    html_preview_url?: string;
    html_content?: string;
    has_html: boolean;
  }> {
    const response = await this.request<{
      page_id: string;
      html_preview_url?: string;
      html_content?: string;
      has_html: boolean;
    }>(`/api/v1/documents/${documentId}/pages/${pageId}/preview`);

    if (!response.success) {
      throw new Error(response.error || '获取页面预览失败');
    }

    const data = response.data!;
    return {
      ...data,
      html_content: this.normalizeHtmlAssetUrls(data?.html_content),
    };
  }

  async chatEditHtml(
    documentId: string,
    pageId: string,
    instruction: string,
    currentHtml: string,
    history: ChatHistoryItem[] = [],
    referencePageId?: string,
    referenceHtml?: string
  ): Promise<ChatEditHtmlResponse> {
    const response = await this.request<ChatEditHtmlResponse>('/api/v1/chat/edit-html', {
      method: 'POST',
      timeout: 120000,
      data: JSON.stringify({
        document_id: documentId,
        page_id: pageId,
        instruction,
        current_html: currentHtml,
        history,
        reference_page_id: referencePageId,
        reference_html: referenceHtml
      }),
    });

    if (!response.success) {
      throw new Error(response.error || 'AI修改HTML失败');
    }

    return response.data!;
  }

  async savePageHtml(documentId: string, pageId: string, htmlContent: string): Promise<SavePageHtmlResponse> {
    const response = await this.request<SavePageHtmlResponse>(`/api/v1/documents/${documentId}/pages/${pageId}/html`, {
        method: 'PUT',
        data: JSON.stringify({ html_content: htmlContent }),
    });
    if (!response.success) throw new Error(response.error || '保存HTML失败');
    return response.data!;
  }

  async updatePageHtml(pageSpecId: string, htmlContent: string): Promise<Record<string, unknown>> {
    const response = await this.request<Record<string, unknown>>(`/api/v1/page-specs/${pageSpecId}/html`, {
      method: 'PUT',
      data: JSON.stringify({ html_content: htmlContent }),
    });

    if (!response.success) {
      throw new Error(response.error || '更新页面HTML失败');
    }

    return response.data!;
  }

  async getPageHtmlHistory(pageSpecId: string): Promise<HTMLPage[]> {
    const response = await this.request<HTMLPage[]>(`/api/v1/page-specs/${pageSpecId}/html-history`);

    if (!response.success) {
      throw new Error(response.error || '获取页面HTML历史失败');
    }

    return response.data!;
  }

  /**
   * 删除页面规格
   */
  async deletePageSpec(pageSpecId: string): Promise<boolean> {
    const response = await this.request<{ deleted: boolean }>(`/api/v1/page-specs/${pageSpecId}`, {
      method: 'DELETE',
    });

    if (!response.success) {
      throw new Error(response.error || '删除页面规格失败');
    }

    return response.data!.deleted;
  }

  async getPages(documentId: string): Promise<Page[]> {
    const response = await this.request<{
      document_id: string;
      pages: Page[];
      pagination?: Record<string, unknown>;
    } | Page[]>(`/api/v1/documents/${documentId}/pages`);

    if (!response.success) {
      throw new Error(response.error || '获取页面列表失败');
    }

    const data = response.data!;
    if (Array.isArray(data)) {
      return data;
    }
    return data.pages || [];
  }

  async splitPage(request: PageSplitRequest): Promise<PageSplitResponse> {
    const response = await this.request<PageSplitResponse>('/api/v1/pages/split', {
      method: 'POST',
      data: JSON.stringify(request),
    });

    if (!response.success) {
      throw new Error(response.error || '拆分页面失败');
    }

    return response.data!;
  }

  async mergePages(request: PageMergeRequest): Promise<PageMergeResponse> {
    const response = await this.request<PageMergeResponse>('/api/v1/pages/merge', {
      method: 'POST',
      data: JSON.stringify(request),
    });

    if (!response.success) {
      throw new Error(response.error || '合并页面失败');
    }

    return response.data!;
  }

  async reorderPages(request: PageReorderRequest): Promise<void> {
    const response = await this.request<{ success: boolean }>('/api/v1/pages/reorder', {
      method: 'POST',
      data: JSON.stringify(request),
    });

    if (!response.success) {
      throw new Error(response.error || '重新排序页面失败');
    }
  }

  async updatePageContent(pageId: string, content: PageContent): Promise<Page> {
    const response = await this.request<Page>(`/api/v1/pages/${pageId}/content`, {
      method: 'PUT',
      data: JSON.stringify(content),
    });

    if (!response.success) {
      throw new Error(response.error || '更新页面内容失败');
    }

    return response.data!;
  }

  async applyThemeToPage(pageId: string, theme: ThemeConfig): Promise<Page> {
    const response = await this.request<Page>(`/api/v1/pages/${pageId}/theme`, {
      method: 'PUT',
      data: JSON.stringify({ theme }),
    });

    if (!response.success) {
      throw new Error(response.error || '应用主题失败');
    }

    return response.data!;
  }

  /**
   * 获取AI分页结果
   */
  async getAISegmentationResult(documentId: string): Promise<SegmentationResponse> {
    const response = await this.request<SegmentationResponse>(`/api/v1/documents/${documentId}/ai-segmentation`);

    if (!response.success) {
      throw new Error(response.error || '获取AI分页结果失败');
    }

    return response.data!;
  }

  /**
   * 触发AI分页处理
   */
  async triggerAISegmentation(
    documentId: string,
    payload?: {
      strategy?: 'auto' | 'deepseek' | 'marker'
      page_plan_id?: string
      marker?: string
      marker_regex?: string
      ignore_empty_pages?: boolean
      pagination_preferences?: string
    }
  ): Promise<SegmentationResponse> {
    const response = await this.request<SegmentationResponse>(`/api/v1/documents/${documentId}/ai-segmentation`, {
      method: 'POST',
      data: payload ? JSON.stringify(payload) : undefined,
    });

    if (!response.success) {
      throw new Error(response.error || '触发AI分页处理失败');
    }

    return response.data!;
  }

  /**
   * 触发HTML页面生成
   * 对应SinglePageGenerator.generate_batch
   */
  async generatePages(pagePlanId: string, designSpec: DesignSpec, options: Record<string, unknown> = {}): Promise<GenerationTask> {
    const response = await this.request<GenerationTask>('/api/v1/generate', {
      method: 'POST',
      data: JSON.stringify({
        page_plan_id: pagePlanId,
        design_spec: designSpec,
        options,
      }),
    });

    if (!response.success) {
      throw new Error(response.error || '触发生成失败');
    }

    return response.data!;
  }

  async generatePageHtml(
    pageSpecId: string,
    designSpec: DesignSpec,
    payload: {
      options?: Record<string, unknown>
      content_override?: string
      save_content?: boolean
      instructions?: string
    } = {}
  ): Promise<GenerationTask> {
    const response = await this.request<GenerationTask>(`/api/v1/generate/page-specs/${pageSpecId}`, {
      method: 'POST',
      data: JSON.stringify({
        design_spec: designSpec,
        options: payload.options,
        content_override: payload.content_override,
        save_content: payload.save_content,
        instructions: payload.instructions,
      }),
    });

    if (!response.success) {
      throw new Error(response.error || '触发生成失败');
    }

    return response.data!;
  }

  /**
   * 查询任务状态
   * 对应后端任务监控
   */
  private toGenerationTask(data: Task | Record<string, unknown> | undefined): GenerationTask {
    const record = (data || {}) as Record<string, unknown>;
    const errorData = (record.error_data && typeof record.error_data === 'object')
      ? record.error_data as Record<string, unknown>
      : {};
    const display = resolveTaskDisplay(record);
    return {
      task_id: String(record.id || record.task_id || ''),
      id: typeof record.id === 'string' ? record.id : undefined,
      status: String(record.status || 'pending') as GenerationTask['status'],
      display_status: display.display_status,
      display_label: display.display_label,
      progress: typeof record.progress === 'number' ? record.progress : 0,
      total_pages: typeof record.total_pages === 'number' ? record.total_pages : 0,
      completed_pages: typeof record.completed_pages === 'number' ? record.completed_pages : 0,
      message: display.display_message,
      request_data: (record.request_data && typeof record.request_data === 'object')
        ? record.request_data as GenerationTask['request_data']
        : undefined,
      result_data: (record.result_data && typeof record.result_data === 'object')
        ? record.result_data as GenerationTask['result_data']
        : undefined,
      error: typeof errorData.message === 'string'
        ? errorData.message
        : (typeof record.message === 'string' && String(record.status || '').toLowerCase() === 'failed' ? record.message : undefined),
      created_at: typeof record.created_at === 'string' ? record.created_at : new Date().toISOString(),
      completed_at: typeof record.completed_at === 'string' ? record.completed_at : undefined,
      updated_at: typeof record.updated_at === 'string' ? record.updated_at : new Date().toISOString(),
    };
  }

  async getTaskStatus(taskId: string): Promise<GenerationTask> {
    const response = await this.request<Task>(`/api/v1/tasks/${taskId}`);

    if (!response.success) {
      throw new Error(response.error || '查询任务状态失败');
    }

    return this.toGenerationTask(response.data);
  }

  private toExportTaskSubmission(data: Record<string, unknown> | undefined): ExportTaskSubmission {
    return {
      task_id: String(data?.task_id || ''),
      status: String(data?.status || 'pending') as ExportTaskSubmission['status'],
      message: String(data?.message || '导出任务已提交'),
      created_at: typeof data?.created_at === 'string' ? data.created_at : undefined,
      completed_at: typeof data?.completed_at === 'string' ? data.completed_at : undefined,
      reuse_hit: Boolean(data?.reuse_hit),
      queue_position: typeof data?.queue_position === 'number' ? data.queue_position : null,
      file_url: typeof data?.file_url === 'string' ? data.file_url : undefined,
      file_size: typeof data?.file_size === 'number' ? data.file_size : undefined,
    };
  }

  /**
   * 单页重新生成
   * 对应SinglePageGenerator.generate
   */
  async regeneratePage(pageId: string, instructions: string): Promise<PageSpec> {
    const response = await this.request<PageSpec>('/api/v1/regenerate', {
      method: 'POST',
      data: JSON.stringify({
        page_id: pageId,
        instructions: instructions,
      }),
    });

    if (!response.success) {
      throw new Error(response.error || '重新生成失败');
    }

    return response.data!;
  }

  /**
   * 导出PDF
   * 对应后端PDF导出
   */
  async exportToPDF(htmlSetVersionId: string, options: Record<string, unknown> = {}): Promise<ExportTaskSubmission> {
    const response = await this.request<Record<string, unknown>>(
      '/api/v1/export/pdf',
      {
      method: 'POST',
      data: JSON.stringify({ html_set_version_id: htmlSetVersionId, ...options }),
      timeout: 60000,
    });

    if (!response.success) {
      throw new Error(response.error || '导出失败');
    }
    return this.toExportTaskSubmission(response.data || {});
  }

  /**
   * 导出PPTX
   */
  async exportToPPTX(htmlSetVersionId: string, options: Record<string, unknown> = {}): Promise<ExportTaskSubmission> {
    const response = await this.request<Record<string, unknown>>(
      '/api/v1/export/pptx',
      {
      method: 'POST',
      data: JSON.stringify({ html_set_version_id: htmlSetVersionId, ...options }),
      timeout: 60000,
    });

    if (!response.success) {
      throw new Error(response.error || '导出PPTX失败');
    }
    return this.toExportTaskSubmission(response.data || {});
  }

  /**
   * 获取系统状态
   */
  async getSystemStatus(): Promise<StatusResponse> {
    const response = await this.request<StatusResponse>('/api/v1/status');

    if (!response.success) {
      throw new Error(response.error || '获取系统状态失败');
    }

    return response.data!;
  }

  /**
   * 取消任务
   */
  async cancelTask(taskId: string): Promise<void> {
    const response = await this.request<void>(`/api/v1/tasks/${taskId}/cancel`, {
      method: 'POST',
    });

    if (!response.success) {
      throw new Error(response.error || '取消任务失败');
    }
  }

  /**
   * 获取任务历史
   */
  async getTaskHistory(
    limitOrOptions: number | {
      limit?: number;
      status?: string;
      taskType?: string;
      documentId?: string;
    } = 10
  ): Promise<GenerationTask[]> {
    const options = typeof limitOrOptions === 'number'
      ? { limit: limitOrOptions }
      : limitOrOptions;
    const params = new URLSearchParams();
    params.set('limit', String(options.limit ?? 10));
    if (options.status) {
      params.set('status', options.status);
    }
    if (options.taskType) {
      params.set('task_type', options.taskType);
    }
    if (options.documentId) {
      params.set('document_id', options.documentId);
    }
    const response = await this.request<Task[]>(`/api/v1/tasks/history?${params.toString()}`);

    if (!response.success) {
      throw new Error(response.error || '获取任务历史失败');
    }

    return (response.data || []).map((item) => this.toGenerationTask(item));
  }

  /**
   * 获取工作流列表（文档列表）
   */
  async getWorkflows(): Promise<Document[]> {
    const response = await this.request<Document[]>('/api/v1/documents');

    if (!response.success) {
      throw new Error(response.error || '获取工作流列表失败');
    }

    return response.data!;
  }

  /**
    * 获取系统统计信息
    */
   async getStats(): Promise<DashboardStats> {
     const response = await this.request<DashboardStats>('/api/v1/stats');
 
     if (!response.success) {
       throw new Error(response.error || '获取统计信息失败');
     }
 
     return response.data!;
   }

  // ========== 数据库相关方法 ==========

  async getAllDocumentsDB(filters?: DocumentFilters): Promise<Document[]> {
    const queryParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value);
        }
      });
    }

    const endpoint = `/api/v1/documents${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await this.request<Document[]>(endpoint);

    if (!response.success) {
      throw new Error(response.error || '获取文档失败');
    }

    return response.data!;
  }

  async searchDocumentsDB(searchRequest: SearchDocumentsRequest): Promise<PaginatedResponse<Document>> {
    const response = await this.request<PaginatedResponse<Document>>('/api/v1/documents/search', {
      method: 'POST',
      data: JSON.stringify(searchRequest),
    });

    if (!response.success) {
      throw new Error(response.error || '搜索文档失败');
    }

    return response.data!;
  }

  /**
   * 创建文档（数据库版本）
   */
  async createDocumentDB(documentData: Partial<Document>): Promise<Document> {
    const response = await this.request<Document>('/api/v1/documents/', {
      method: 'POST',
      data: JSON.stringify(documentData),
    });

    if (!response.success) {
      throw new Error(response.error || '创建文档失败');
    }

    return response.data!;
  }

  /**
   * 获取文档详情（数据库版本）
   */
  async getDocumentDB(documentId: string): Promise<Document> {
    const response = await this.request<Document>(`/api/v1/documents/${documentId}`);

    if (!response.success) {
      throw new Error(response.error || '获取文档失败');
    }

    return response.data!;
  }

  async getDocumentShareContext(documentId: string): Promise<DocumentShareContext> {
    const response = await this.request<DocumentShareContext>(`/api/v1/documents/${documentId}/share-context`);

    if (!response.success) {
      throw new Error(response.error || '获取工作流分享上下文失败');
    }

    return response.data!;
  }

  async updateDocumentDB(documentId: string, updates: Partial<Document>): Promise<Document> {
    const response = await this.request<Document>(`/api/v1/documents/${documentId}`, {
      method: 'PUT',
      data: JSON.stringify(updates),
    });

    if (!response.success) {
      throw new Error(response.error || '更新文档失败');
    }

    return response.data!;
  }

  async deleteDocumentDB(documentId: string): Promise<boolean> {
    const response = await this.request<{ deleted: boolean }>(`/api/v1/documents/${documentId}`, {
      method: 'DELETE',
    });

    if (!response.success) {
      throw new Error(response.error || '删除文档失败');
    }

    return Boolean(response.data?.deleted);
  }

  async getDeletedDocumentsDB(limit?: number): Promise<Document[]> {
    const query = typeof limit === 'number' ? `?limit=${limit}` : '';
    const response = await this.request<Document[]>(`/api/v1/documents/recycled${query}`);

    if (!response.success) {
      throw new Error(response.error || '获取回收站文档失败');
    }

    return response.data!;
  }

  async restoreDocumentDB(documentId: string): Promise<RestoreDocumentResult> {
    const response = await this.request<RestoreDocumentResult>(`/api/v1/documents/${documentId}/restore`, {
      method: 'POST',
    });

    if (!response.success) {
      throw new Error(response.error || '恢复文档失败');
    }

    return response.data!;
  }

  async purgeDocumentDB(documentId: string): Promise<{ purged: boolean; deleted_files?: string[]; message?: string; status?: string }> {
    const response = await this.request<{ purged: boolean; deleted_files?: string[]; message?: string; status?: string }>(
      `/api/v1/documents/${documentId}/purge`,
      {
        method: 'DELETE',
      }
    );

    if (!response.success) {
      throw new Error(response.error || '永久删除文档失败');
    }

    return response.data!;
  }

  async batchRestoreDeletedDocumentsDB(documentIds: string[]): Promise<RecycleBatchActionResult> {
    const response = await this.request<RecycleBatchActionResult>('/api/v1/documents/recycled/actions/restore', {
      method: 'POST',
      data: JSON.stringify({ document_ids: documentIds }),
    });

    if (!response.success) {
      throw new Error(response.error || '批量恢复文档失败');
    }

    return response.data!;
  }

  async batchPurgeDeletedDocumentsDB(documentIds: string[]): Promise<RecycleBatchActionResult> {
    const response = await this.request<RecycleBatchActionResult>('/api/v1/documents/recycled/actions/purge', {
      method: 'POST',
      data: JSON.stringify({ document_ids: documentIds }),
    });

    if (!response.success) {
      throw new Error(response.error || '批量永久删除文档失败');
    }

    return response.data!;
  }

  async emptyRecycleBinDB(): Promise<RecycleBatchActionResult> {
    const response = await this.request<RecycleBatchActionResult>('/api/v1/documents/recycled/empty', {
      method: 'POST',
    });

    if (!response.success) {
      throw new Error(response.error || '清空回收站失败');
    }

    return response.data!;
  }

  /**
   * 更新文档状态（数据库版本）
   */
  async updateDocumentStatusDB(documentId: string, status: string, currentStep: string): Promise<Document> {
    const response = await this.request<Document>(`/api/v1/documents/${documentId}/status`, {
      method: 'PUT',
      data: JSON.stringify({ status, current_step: currentStep }),
    });

    if (!response.success) {
      throw new Error(response.error || '更新文档状态失败');
    }

    return response.data!;
  }

  async getDocumentWorkflowEventsDB(documentId: string, limit: number = 50): Promise<WorkflowEvent[]> {
    const response = await this.request<WorkflowEvent[]>(
      `/api/v1/documents/${documentId}/workflow-events?limit=${limit}`
    );

    if (!response.success) {
      throw new Error(response.error || '获取工作流事件失败');
    }

    return response.data!;
  }

  async createWorkflowEventDB(eventData: WorkflowEventInput): Promise<WorkflowEvent> {
    const response = await this.request<WorkflowEvent>(
      `/api/v1/documents/${eventData.document_id}/workflow-events`,
      {
        method: 'POST',
        data: JSON.stringify(eventData),
      }
    );

    if (!response.success) {
      throw new Error(response.error || '创建工作流事件失败');
    }

    return response.data!;
  }

  /**
   * 创建任务（数据库版本）
   */
  async createTaskDB(taskData: Partial<Task>): Promise<Task> {
    const response = await this.request<Task>('/api/v1/tasks', {
      method: 'POST',
      data: JSON.stringify(taskData),
    });

    if (!response.success) {
      throw new Error(response.error || '创建任务失败');
    }

    return response.data!;
  }

  /**
   * 更新任务进度（数据库版本）
   */
  async updateTaskProgressDB(taskId: string, progress: number, message?: string): Promise<Task> {
    const response = await this.request<Task>(`/api/v1/tasks/${taskId}/progress`, {
      method: 'PUT',
      data: JSON.stringify({ progress, message }),
    });

    if (!response.success) {
      throw new Error(response.error || '更新任务进度失败');
    }

    return response.data!;
  }

  async completeTaskDB(taskId: string, resultData?: Record<string, unknown>): Promise<Task> {
    const response = await this.request<Task>(`/api/v1/tasks/${taskId}/complete`, {
      method: 'PUT',
      data: JSON.stringify({ result_data: resultData }),
    });

    if (!response.success) {
      throw new Error(response.error || '完成任务失败');
    }

    return response.data!;
  }

  async getDatabaseHealthDB(): Promise<DatabaseHealth> {
    const response = await this.request<DatabaseHealth>('/api/v1/system/health');

    if (!response.success) {
      throw new Error(response.error || '获取数据库健康状态失败');
    }

    return response.data!;
  }

  async getActiveDesignSpecsDB(): Promise<Record<string, DatabaseDesignSpec[]>> {
    const response = await this.request<Record<string, DatabaseDesignSpec[]>>('/api/v1/designs/active');

    if (!response.success) {
      throw new Error(response.error || '获取设计配置失败');
    }

    return response.data!;
  }

  async getAllDesignSpecsDB(): Promise<DatabaseDesignSpec[]> {
    const response = await this.request<DatabaseDesignSpec[]>('/api/v1/designs');

    if (!response.success) {
      throw new Error(response.error || '获取设计配置失败');
    }

    return response.data!;
  }

  async getLogoPresets(): Promise<LogoPresetItem[]> {
    const response = await this.request<{ items: LogoPresetItem[] }>('/api/v1/logos/presets');
    if (!response.success) {
      throw new Error(response.error || '获取预置 Logo 失败');
    }
    return response.data?.items || [];
  }

  async getLogoHistory(limit = 20): Promise<LogoHistoryResponse> {
    const response = await this.request<LogoHistoryResponse>('/api/v1/logos/history', {
      params: { limit },
    });
    if (!response.success) {
      throw new Error(response.error || '获取 Logo 历史失败');
    }
    return response.data || { history: [], recommended: [] };
  }

  async uploadLogo(file: File): Promise<LogoUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await this.axiosInstance.post('/api/v1/logos/upload', formData);
      const responseData = response.data;
      if (responseData.success === false) {
        throw new Error(responseData.error || '上传 Logo 失败');
      }
      return responseData.data as LogoUploadResult;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ error?: string; detail?: string }>;
      const errorMessage = axiosError.response?.data?.error
        || axiosError.response?.data?.detail
        || axiosError.message
        || '上传 Logo 失败';
      throw new Error(errorMessage);
    }
  }

  /**
   * 获取系统统计信息（数据库版本）
   */
  async getSystemStatsDB(): Promise<SystemStats> {
    const response = await this.request<SystemStats>('/api/v1/system/stats');

    if (!response.success) {
      throw new Error(response.error || '获取系统统计信息失败');
    }

    return response.data!;
  }

  /**
   * 检查数据库是否启用
   */
  async checkDatabaseEnabled(): Promise<boolean> {
    try {
      logger.debug('检查数据库启用状态...', 'APIClient');
      const stats = await this.getSystemStatsDB();
      const enabled = stats.database_enabled && stats.database_initialized;
      logger.debug(`数据库启用状态: ${enabled}`, {
        database_enabled: stats.database_enabled,
        database_initialized: stats.database_initialized,
      }, 'APIClient');
      return enabled;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      logger.warn('检查数据库启用状态失败，使用内存模式', {
        message: apiError.message,
        status: apiError.status,
        code: apiError.code,
      }, 'APIClient');
      return false;
    }
  }

  async login(username: string, password: string): Promise<AuthLoginResult> {
    const response = await this.request<AuthLoginResult>('/api/v1/auth/login', {
      method: 'POST',
      data: JSON.stringify({ username, password }),
    });

    if (!response.success) {
      throw new Error(response.error || '登录失败');
    }

    const data = response.data!;
    setStoredUser(data.user);
    return data;
  }

  async getMe(): Promise<AuthUserInfo> {
    const response = await this.request<AuthUserInfo>('/api/v1/auth/me');

    if (!response.success) {
      throw new Error(response.error || '获取用户信息失败');
    }

    const data = response.data!;
    setStoredUser(data);
    return data;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/api/v1/auth/logout', { method: 'POST' });
    } catch {
      // ignore
    } finally {
      this.invalidateUsageSummaryCache();
      clearAccessToken();
    }
  }

  async listUsers(params?: {
    q?: string;
    role?: UserRole;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<AdminListUsersResult> {
    const response = await this.request<AdminListUsersResult>('/api/v1/users', {
      params,
    });

    if (!response.success) {
      throw new Error(response.error || '获取用户列表失败');
    }

    return response.data!;
  }

  async createUser(payload: { username: string; password: string; role?: UserRole; is_active?: boolean }): Promise<AdminUserItem> {
    const response = await this.request<AdminUserItem>('/api/v1/users', {
      method: 'POST',
      data: JSON.stringify({
        username: payload.username,
        password: payload.password,
        role: payload.role ?? 'user',
        is_active: payload.is_active ?? true,
      }),
    });

    if (!response.success) {
      throw new Error(response.error || '创建用户失败');
    }

    return response.data!;
  }

  async disableUser(userId: string): Promise<AdminUserItem> {
    const response = await this.request<AdminUserItem>(`/api/v1/users/${encodeURIComponent(userId)}/disable`, {
      method: 'POST',
    });

    if (!response.success) {
      throw new Error(response.error || '禁用用户失败');
    }

    return response.data!;
  }

  async enableUser(userId: string): Promise<AdminUserItem> {
    const response = await this.request<AdminUserItem>(`/api/v1/users/${encodeURIComponent(userId)}/enable`, {
      method: 'POST',
    });

    if (!response.success) {
      throw new Error(response.error || '启用用户失败');
    }

    return response.data!;
  }

  async resetUserPassword(userId: string, newPassword: string): Promise<AdminUserItem> {
    const response = await this.request<AdminUserItem>(`/api/v1/users/${encodeURIComponent(userId)}/reset-password`, {
      method: 'POST',
      data: JSON.stringify({ new_password: newPassword }),
    });

    if (!response.success) {
      throw new Error(response.error || '重置密码失败');
    }

    return response.data!;
  }

  async setUserRole(userId: string, role: UserRole): Promise<AdminUserItem> {
    const response = await this.request<AdminUserItem>(`/api/v1/users/${encodeURIComponent(userId)}/set-role`, {
      method: 'POST',
      data: JSON.stringify({ role }),
    });

    if (!response.success) {
      throw new Error(response.error || '设置角色失败');
    }

    return response.data!;
  }

  async deleteUser(userId: string): Promise<AdminUserItem> {
    const response = await this.request<AdminUserItem>(`/api/v1/users/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });

    if (!response.success) {
      throw new Error(response.error || '删除用户失败');
    }

    return response.data!;
  }

  async getMyUsageSummary(): Promise<UsageSummary> {
    const now = Date.now();
    if (this.usageSummaryCache && this.usageSummaryCache.expiresAt > now) {
      return this.usageSummaryCache.value;
    }
    if (this.usageSummaryInflight) {
      return this.usageSummaryInflight;
    }

    this.usageSummaryInflight = this.request<UsageSummary>('/api/v1/usage/me')
      .then((response) => {
        if (!response.success) {
          throw new Error(response.error || '获取额度摘要失败');
        }
        const value = response.data!;
        // 短 TTL 缓存用于吸收路由切换抖动请求
        this.usageSummaryCache = {
          value,
          expiresAt: Date.now() + 30_000,
        };
        return value;
      })
      .finally(() => {
        this.usageSummaryInflight = null;
      });

    return this.usageSummaryInflight;
  }

  async getMyUsageLedger(limit = 20, offset = 0): Promise<{ items: UsageLedgerEntry[]; limit: number; offset: number }> {
    const response = await this.request<{ items: UsageLedgerEntry[]; limit: number; offset: number }>('/api/v1/usage/me/ledger', {
      params: { limit, offset },
    });
    if (!response.success) {
      throw new Error(response.error || '获取额度流水失败');
    }
    return response.data!;
  }

  async getMyUsageEvents(limit = 20, offset = 0): Promise<{ items: UsageMeteringEvent[]; limit: number; offset: number }> {
    const response = await this.request<{ items: UsageMeteringEvent[]; limit: number; offset: number }>('/api/v1/usage/me/events', {
      params: { limit, offset },
    });
    if (!response.success) {
      throw new Error(response.error || '获取额度事件失败');
    }
    return response.data!;
  }

  async estimateUsage(payload: {
    event_type: string;
    page_type?: string;
    model_name?: string;
    model_tier?: string;
    page_count?: number;
  }): Promise<UsageEstimateResult> {
    const response = await this.request<UsageEstimateResult>('/api/v1/usage/estimate', {
      method: 'POST',
      data: JSON.stringify(payload),
    });
    if (!response.success) {
      throw new Error(response.error || '估算额度消耗失败');
    }
    return response.data!;
  }

  async listUsageAccounts(limit = 200, offset = 0): Promise<AdminUsageAccountListResult> {
    const response = await this.request<AdminUsageAccountListResult>('/api/v1/admin/usage/accounts', {
      params: { limit, offset },
    });
    if (!response.success) {
      throw new Error(response.error || '获取额度账户失败');
    }
    return response.data!;
  }

  async grantUsagePoints(accountId: string, points: number, remark?: string): Promise<{ account: UsageSummary }> {
    const response = await this.request<{ account: UsageSummary }>(`/api/v1/admin/usage/accounts/${encodeURIComponent(accountId)}/grant`, {
      method: 'POST',
      data: JSON.stringify({ points, remark }),
    });
    if (!response.success) {
      throw new Error(response.error || '发放点数失败');
    }
    this.invalidateUsageSummaryCache();
    return response.data!;
  }

  async adjustUsagePoints(accountId: string, points: number, remark?: string): Promise<{ account: UsageSummary }> {
    const response = await this.request<{ account: UsageSummary }>(`/api/v1/admin/usage/accounts/${encodeURIComponent(accountId)}/adjust`, {
      method: 'POST',
      data: JSON.stringify({ points, remark }),
    });
    if (!response.success) {
      throw new Error(response.error || '调整点数失败');
    }
    this.invalidateUsageSummaryCache();
    return response.data!;
  }

  async batchGrantUsagePoints(
    items: BatchUsageAdjustmentItem[],
    remark?: string
  ): Promise<BatchUsageAdjustmentResult> {
    const response = await this.request<BatchUsageAdjustmentResult>('/api/v1/admin/usage/accounts/batch-grant', {
      method: 'POST',
      data: JSON.stringify({ items, remark, mode: 'grant' }),
    });
    if (!response.success) {
      throw new Error(response.error || '批量发放点数失败');
    }
    this.invalidateUsageSummaryCache();
    return response.data!;
  }

  async batchAdjustUsagePoints(
    items: BatchUsageAdjustmentItem[],
    remark?: string
  ): Promise<BatchUsageAdjustmentResult> {
    const response = await this.request<BatchUsageAdjustmentResult>('/api/v1/admin/usage/accounts/batch-adjust', {
      method: 'POST',
      data: JSON.stringify({ items, remark, mode: 'adjust' }),
    });
    if (!response.success) {
      throw new Error(response.error || '批量调整点数失败');
    }
    this.invalidateUsageSummaryCache();
    return response.data!;
  }

  async getGeminiQuotaSummary(): Promise<GeminiQuotaSummary> {
    const response = await this.request<GeminiQuotaSummary>('/api/v1/admin/usage/gemini/quota');
    if (!response.success) {
      throw new Error(response.error || '获取 Gemini 配额信息失败');
    }
    return response.data!;
  }

  async updateGeminiQuotaSettings(payload: {
    total_quota_calls: number;
    low_balance_threshold?: number;
    alert_cooldown_calls?: number;
  }): Promise<GeminiQuotaSummary> {
    const response = await this.request<GeminiQuotaSummary>('/api/v1/admin/usage/gemini/quota', {
      method: 'PUT',
      data: JSON.stringify(payload),
    });
    if (!response.success) {
      throw new Error(response.error || '更新 Gemini 配额配置失败');
    }
    return response.data!;
  }
}

// 全局API客户端实例
export const apiClient = new APIClient();

// 导出快捷方法
export const api = {
  login: (username: string, password: string) => apiClient.login(username, password),
  getMe: () => apiClient.getMe(),
  logout: () => apiClient.logout(),
  uploadDocument: (file: File) => apiClient.uploadDocument(file),
  resolveUploadRouting: (
    payload: {
      decision_token: string;
      action: 'attach_active' | 'restore_deleted' | 'create_new';
      target_document_id?: string;
    }
  ) => apiClient.resolveUploadRouting(payload),
  createWorkflowShare: (
    payload: {
      document_id: string;
      target_user_id?: string;
      target_username?: string;
      note?: string;
    }
  ) => apiClient.createWorkflowShare(payload),
  getIncomingWorkflowShares: (status?: WorkflowShareStatus) => apiClient.getIncomingWorkflowShares(status),
  getOutgoingWorkflowShares: (status?: WorkflowShareStatus) => apiClient.getOutgoingWorkflowShares(status),
  acceptWorkflowShare: (shareId: string) => apiClient.acceptWorkflowShare(shareId),
  rejectWorkflowShare: (shareId: string) => apiClient.rejectWorkflowShare(shareId),
  getShareCandidates: (query?: string, limit?: number) => apiClient.getShareCandidates(query, limit),
  getDesignSpecs: () => apiClient.getDesignSpecs(),
  getLayoutConfig: (layoutName: string) => apiClient.getLayoutConfig(layoutName),
  getThemeConfig: (themeName: string) => apiClient.getThemeConfig(themeName),
  getTypographyConfig: (typographyName: string) => apiClient.getTypographyConfig(typographyName),
  generatePages: (pagePlanId: string, designSpec: DesignSpec, options?: Record<string, unknown>) => apiClient.generatePages(pagePlanId, designSpec, options),
  generatePageHtml: (
    pageSpecId: string,
    designSpec: DesignSpec,
    payload?: {
      options?: Record<string, unknown>
      content_override?: string
      save_content?: boolean
      instructions?: string
    }
  ) => apiClient.generatePageHtml(pageSpecId, designSpec, payload),
  getTaskStatus: (taskId: string) => apiClient.getTaskStatus(taskId),
  regeneratePage: (pageId: string, instructions: string) => apiClient.regeneratePage(pageId, instructions),
  exportToPDF: (htmlSetVersionId: string, options?: Record<string, unknown>) => apiClient.exportToPDF(htmlSetVersionId, options),
  exportToPPTX: (htmlSetVersionId: string, options?: Record<string, unknown>) => apiClient.exportToPPTX(htmlSetVersionId, options),
  getSystemStatus: () => apiClient.getSystemStatus(),
  cancelTask: (taskId: string) => apiClient.cancelTask(taskId),
  getTaskHistory: (limitOrOptions?: number | { limit?: number; status?: string; taskType?: string; documentId?: string }) => apiClient.getTaskHistory(limitOrOptions),
  getWorkflows: () => apiClient.getWorkflows(),
  // 数据库相关方法
  createDocumentDB: (documentData: Partial<Document>) => apiClient.createDocumentDB(documentData),
  getDocumentDB: (documentId: string) => apiClient.getDocumentDB(documentId),
  getDocumentShareContext: (documentId: string) => apiClient.getDocumentShareContext(documentId),
  deleteDocumentDB: (documentId: string) => apiClient.deleteDocumentDB(documentId),
  getDeletedDocumentsDB: (limit?: number) => apiClient.getDeletedDocumentsDB(limit),
  restoreDocumentDB: (documentId: string) => apiClient.restoreDocumentDB(documentId),
  purgeDocumentDB: (documentId: string) => apiClient.purgeDocumentDB(documentId),
  batchRestoreDeletedDocumentsDB: (documentIds: string[]) => apiClient.batchRestoreDeletedDocumentsDB(documentIds),
  batchPurgeDeletedDocumentsDB: (documentIds: string[]) => apiClient.batchPurgeDeletedDocumentsDB(documentIds),
  emptyRecycleBinDB: () => apiClient.emptyRecycleBinDB(),
  updateDocumentStatusDB: (documentId: string, status: string, currentStep: string) => apiClient.updateDocumentStatusDB(documentId, status, currentStep),
  createTaskDB: (taskData: Partial<Task>) => apiClient.createTaskDB(taskData),
  updateTaskProgressDB: (taskId: string, progress: number, message?: string) => apiClient.updateTaskProgressDB(taskId, progress, message),
  getSystemStatsDB: () => apiClient.getSystemStatsDB(),
  checkDatabaseEnabled: () => apiClient.checkDatabaseEnabled(),
  listUsers: (params?: { q?: string; role?: UserRole; is_active?: boolean; limit?: number; offset?: number }) => apiClient.listUsers(params),
  createUser: (payload: { username: string; password: string; role?: UserRole; is_active?: boolean }) => apiClient.createUser(payload),
  disableUser: (userId: string) => apiClient.disableUser(userId),
  enableUser: (userId: string) => apiClient.enableUser(userId),
  resetUserPassword: (userId: string, newPassword: string) => apiClient.resetUserPassword(userId, newPassword),
  setUserRole: (userId: string, role: UserRole) => apiClient.setUserRole(userId, role),
  deleteUser: (userId: string) => apiClient.deleteUser(userId),
  getMyUsageSummary: () => apiClient.getMyUsageSummary(),
  getMyUsageLedger: (limit?: number, offset?: number) => apiClient.getMyUsageLedger(limit, offset),
  getMyUsageEvents: (limit?: number, offset?: number) => apiClient.getMyUsageEvents(limit, offset),
  estimateUsage: (payload: { event_type: string; page_type?: string; model_name?: string; model_tier?: string; page_count?: number }) =>
    apiClient.estimateUsage(payload),
  listUsageAccounts: (limit?: number, offset?: number) => apiClient.listUsageAccounts(limit, offset),
  grantUsagePoints: (accountId: string, points: number, remark?: string) => apiClient.grantUsagePoints(accountId, points, remark),
  adjustUsagePoints: (accountId: string, points: number, remark?: string) => apiClient.adjustUsagePoints(accountId, points, remark),
  batchGrantUsagePoints: (items: BatchUsageAdjustmentItem[], remark?: string) =>
    apiClient.batchGrantUsagePoints(items, remark),
  batchAdjustUsagePoints: (items: BatchUsageAdjustmentItem[], remark?: string) =>
    apiClient.batchAdjustUsagePoints(items, remark),
  getGeminiQuotaSummary: () => apiClient.getGeminiQuotaSummary(),
  updateGeminiQuotaSettings: (payload: {
    total_quota_calls: number;
    low_balance_threshold?: number;
    alert_cooldown_calls?: number;
  }) => apiClient.updateGeminiQuotaSettings(payload),
  // 页面规划相关方法
  createPagePlan: (pagePlanData: CreatePagePlanRequest) => apiClient.createPagePlan(pagePlanData),
  submitPagePlan: (pagePlan: CreatePagePlanRequest) => apiClient.submitPagePlan(pagePlan),
  getPagePlan: (pagePlanId: string) => apiClient.getPagePlan(pagePlanId),
  generatePageSpecs: (pagePlanId: string) => apiClient.generatePageSpecs(pagePlanId),
  optimizePageCopy: (pagePlanId: string, pageSpecId: string, payload: OptimizePageCopyRequest) =>
    apiClient.optimizePageCopy(pagePlanId, pageSpecId, payload),
  updatePagePlan: (pagePlanId: string, pagePlan: Partial<PagePlan>) => apiClient.updatePagePlan(pagePlanId, pagePlan),
  updatePageMasterOverrides: (pageSpecId: string, masterOverrides: Record<string, unknown>) =>
    apiClient.updatePageMasterOverrides(pageSpecId, masterOverrides),
  confirmDesignAnchor: (
    pagePlanId: string,
    payload: {
      page_spec_id: string;
      html_version_id?: string;
      page_number?: number;
      title?: string;
      preset_id?: string;
    }
  ) => apiClient.confirmDesignAnchor(pagePlanId, payload),
  getMasterSnapshot: (pagePlanId: string) => apiClient.getMasterSnapshot(pagePlanId),
  getStyleDriftReport: (pagePlanId: string) => apiClient.getStyleDriftReport(pagePlanId),
  rebuildMasterSnapshot: (pagePlanId: string) => apiClient.rebuildMasterSnapshot(pagePlanId),
  applyMissingItemAction: (
    pagePlanId: string,
    payload: {
      missing_id?: string;
      missing_ids?: string[];
      action: 'ACCEPT_AS_IS' | 'PATCH_EXISTING_PAGE' | 'ADD_NEW_PAGE' | 'REPLAN_STRUCTURE';
      target_page_id?: string;
    }
  ) => apiClient.applyMissingItemAction(pagePlanId, payload),
  updatePresentationStructure: (
    pagePlanId: string,
    payload: {
      ordered_chapter_ids?: string[]
      ordered_content_page_ids?: string[]
      chapter_updates?: Array<{
        chapter_id: string
        chapter_title?: string
        chapter_goal?: string
        chapter_role?: string
        chapter_key?: string
        path?: string[]
        metadata?: Record<string, unknown>
      }>
      page_assignments?: Array<{
        page_spec_id: string
        chapter_id: string
        chapter_title?: string
        chapter_goal?: string
        chapter_role?: string
        chapter_key?: string
        path?: string[]
        metadata?: Record<string, unknown>
      }>
      storyline?: string
      special_slide_policy?: Record<string, unknown>
    }
  ) => apiClient.updatePresentationStructure(pagePlanId, payload),
  updateSpecialSlideSettings: (
    pagePlanId: string,
    payload: {
      special_slide_policy: Record<string, unknown>
      policy_version?: number
    }
  ) => apiClient.updateSpecialSlideSettings(pagePlanId, payload),
  previewPartialRegeneration: (
    pagePlanId: string,
    payload: { page_spec_ids?: string[]; chapter_ids?: string[]; regeneration_mode?: string }
  ) => apiClient.previewPartialRegeneration(pagePlanId, payload),
  regeneratePartial: (
    pagePlanId: string,
    payload: { page_spec_ids?: string[]; chapter_ids?: string[]; regeneration_mode?: string; start_generation_task?: boolean; options?: Record<string, unknown> }
  ) => apiClient.regeneratePartial(pagePlanId, payload),
  deletePagePlan: (pagePlanId: string) => apiClient.deletePagePlan(pagePlanId),
  getDocumentPagePlans: (documentId: string) => apiClient.getDocumentPagePlans(documentId),
  createPageSpec: (pageSpecData: PageSpec) => apiClient.createPageSpec(pageSpecData),
  updatePageSpec: (pageSpecId: string, pageSpecData: Partial<PageSpec>) => apiClient.updatePageSpec(pageSpecId, pageSpecData),
  getPagePreview: (documentId: string, pageId: string) => apiClient.getPagePreview(documentId, pageId),
  updatePageHtml: (pageSpecId: string, htmlContent: string) => apiClient.updatePageHtml(pageSpecId, htmlContent),
  deletePageSpec: (pageSpecId: string) => apiClient.deletePageSpec(pageSpecId),
  getPages: (documentId: string) => apiClient.getPages(documentId),
  splitPage: (request: PageSplitRequest) => apiClient.splitPage(request),
  mergePages: (request: PageMergeRequest) => apiClient.mergePages(request),
  reorderPages: (request: PageReorderRequest) => apiClient.reorderPages(request),
  updatePageContent: (pageId: string, content: PageContent) => apiClient.updatePageContent(pageId, content),
  applyThemeToPage: (pageId: string, theme: ThemeConfig) => apiClient.applyThemeToPage(pageId, theme),
  getAISegmentationResult: (documentId: string) => apiClient.getAISegmentationResult(documentId),
  triggerAISegmentation: (
    documentId: string,
    payload?: {
      strategy?: 'auto' | 'deepseek' | 'marker'
      page_plan_id?: string
      marker?: string
      marker_regex?: string
      ignore_empty_pages?: boolean
      pagination_preferences?: string
    }
  ) => apiClient.triggerAISegmentation(documentId, payload),
  getDocumentStatus: (documentId: string) => apiClient.getDocumentStatus(documentId),
  getHtmlPageVersions: (pageSpecId: string) => apiClient.getHtmlPageVersions(pageSpecId),
  createHtmlPageVersion: (pageSpecId: string, payload: { page_number: number; html_content: string; render_params?: Record<string, unknown>; generation_config?: Record<string, unknown>; model_used?: string; validation_passed?: boolean; validation_notes?: string; assets?: unknown }) =>
    apiClient.createHtmlPageVersion(pageSpecId, payload),
  getHtmlSetVersion: (pagePlanId: string) => apiClient.getHtmlSetVersion(pagePlanId),
  createHtmlSetVersion: (pagePlanId: string, payload: { layout_params?: Record<string, unknown>; render_settings?: Record<string, unknown>; created_by?: string }) =>
    apiClient.createHtmlSetVersion(pagePlanId, payload),
};
